/**
 * Challenges Section — 30-day and 18-month savings challenges.
 * Receives state and handlers from parent (LandingPage).
 */
import React from 'react';
import { Flame, Trophy, Calculator, Coffee, UtensilsCrossed, Tv, ShoppingBag, Wine, Cookie, Car, Rocket, Award, Medal, Star, Crown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ShareButtons from './ShareButtons';
import { challengeCategories, challenge18Milestones } from '../../data/mockData';

const CATEGORY_ICONS = {
  Coffee, UtensilsCrossed, Tv, ShoppingBag, Wine, Car, Cookie
};
const MILESTONE_ICONS = {
  Rocket, Award, Medal, Star, Crown, Trophy
};

const ChallengesSection = ({
  challenge30,
  setChallenge30,
  challenge18,
  setChallenge18,
  selectChallengeCategory,
  calculateCustomChallenge,
  calculate18MonthChallenge,
  scrollToSection
}) => (
  <section className="section challenges-section" data-testid="challenges-section">
    <div className="section-header">
      <h2 className="section-title">Retos de Ahorro</h2>
      <p className="section-subtitle">
        ¿Listo para descubrir cuánto dinero se te va en "pequeños placeres"? 🔥
      </p>
    </div>

    <div className="challenges-grid">
      {/* 30-Day Challenge */}
      <Card className="challenge-card">
        <CardHeader>
          <div className="challenge-header-icon">
            <Flame className="w-12 h-12 text-neon" />
          </div>
          <CardTitle className="challenge-title">Reto de 30 Días</CardTitle>
          <CardDescription className="challenge-description">
            Ese cafecito diario "que no es nada" se convierte en mucho. Haz las cuentas y sorpréndete 😱
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="challenge-prompt">¿Qué te gustaría dejar de gastar por 30 días? <span className="challenge-prompt-hint">(puedes elegir varios) 👇</span></p>

          <div className="challenge-categories">
            {challengeCategories.map((category) => {
              const Icon = CATEGORY_ICONS[category.icon] || Cookie;
              const isActive = challenge30.selectedCategories.includes(category.id);
              return (
                <button
                  key={category.id}
                  onClick={() => selectChallengeCategory(category)}
                  className={`category-button ${isActive ? 'category-active' : ''}`}
                  data-testid={`challenge30-category-${category.id}`}
                  aria-pressed={isActive}
                >
                  {isActive && <span className="category-check" aria-hidden="true">✓</span>}
                  <Icon className="w-5 h-5" />
                  <span className="category-name">{category.name}</span>
                  <span className="category-cost">${category.avgCost}/día</span>
                </button>
              );
            })}
          </div>

          {challenge30.selectedCategories.length > 1 && (
            <div className="challenge-combo-badge" data-testid="challenge30-combo-badge">
              🔥 Combo de {challenge30.selectedCategories.length} gastos seleccionados —
              suma diaria: <strong>${challenge30.dailySavings.toLocaleString('es-MX')}</strong>
            </div>
          )}

          <div className="challenge-custom">
            <label className="input-label">O ajusta tu gasto diario total:</label>
            <div className="custom-input-group">
              <Input
                type="number"
                placeholder="100"
                value={challenge30.customAmount}
                onChange={(e) => setChallenge30({ ...challenge30, customAmount: e.target.value })}
                className="input-field"
                data-testid="challenge30-custom-amount"
              />
              <Button onClick={calculateCustomChallenge} className="button-neon" data-testid="challenge30-calculate">
                Calcular
              </Button>
            </div>
          </div>

          {challenge30.yearlySavings > 0 && (
            <div className="challenge-result-30">
              <div className="result-item">
                <span className="result-label">Ahorrarías en 30 días:</span>
                <span className="result-value">${challenge30.monthlySavings.toLocaleString()}</span>
              </div>
              <div className="result-item highlight">
                <span className="result-label">En 1 año ahorrarías:</span>
                <span className="result-value-big">${challenge30.yearlySavings.toLocaleString()}</span>
              </div>
              <div className="challenge-cta-box">
                <p className="text-sm text-gray-300 mb-2">
                  💡 Imagina invertir esto con el 8% anual... ¡Serían ${Math.round(challenge30.yearlySavings * 1.08).toLocaleString()} después de solo un año!
                </p>
                <Button
                  className="button-neon w-full"
                  onClick={() => scrollToSection('email-capture')}
                >
                  <Flame className="w-4 h-4 mr-2" />
                  Acepto el reto de 30 días
                </Button>
              </div>

              <ShareButtons
                text={`Acepté el Reto 30 Días: $${(challenge30.dailySavings || 0).toLocaleString('es-MX')}/día = $${challenge30.yearlySavings.toLocaleString('es-MX')} en un año 🔥 ¿Y tú? ¿Te animas?`}
                label="¿Tus amigos también pueden? Rétalos:"
                testIdPrefix="challenge30-share"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* 18-Month Challenge */}
      <Card className="challenge-card challenge-18" id="reto-18-meses" data-testid="challenge-18-section">
        <CardHeader>
          <div className="challenge-header-icon">
            <Trophy className="w-12 h-12 text-neon" />
          </div>
          <CardTitle className="challenge-title">Reto de 18 Meses</CardTitle>
          <CardDescription className="challenge-description">
            18 meses de consistencia. Sí, es más tiempo que tus propósitos de año nuevo, pero esta vez va en serio 💪
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="challenge-prompt">Define tu compromiso mensual:</p>

          <div>
            <label className="input-label">Aporte mensual ($)</label>
            <Input
              type="number"
              placeholder="2000"
              value={challenge18.monthlyAmount}
              onChange={(e) => setChallenge18({ ...challenge18, monthlyAmount: e.target.value })}
              className="input-field"
              data-testid="challenge18-monthly-amount"
            />
          </div>

          <div>
            <label className="input-label">Rendimiento anual estimado (%)</label>
            <Input
              type="number"
              placeholder="8"
              value={challenge18.rate}
              onChange={(e) => setChallenge18({ ...challenge18, rate: e.target.value })}
              className="input-field"
              data-testid="challenge18-rate"
            />
          </div>

          <Button onClick={calculate18MonthChallenge} className="button-neon w-full" data-testid="challenge18-calculate">
            <Calculator className="w-4 h-4 mr-2" />
            Calcular mi reto de 18 meses
          </Button>

          {challenge18.withInterest > 0 && (
            <div className="challenge-result-18">
              <div className="result-summary">
                <div className="result-col">
                  <span className="result-label">Ahorro total:</span>
                  <span className="result-value">${Math.round(challenge18.totalSavings).toLocaleString()}</span>
                </div>
                <div className="result-col highlight">
                  <span className="result-label">Con interés compuesto:</span>
                  <span className="result-value-big">${Math.round(challenge18.withInterest).toLocaleString()}</span>
                </div>
              </div>

              <div className="milestones-section">
                <h4 className="milestones-title">🎯 Tus Milestones de Progreso</h4>
                <div className="milestones-grid">
                  {challenge18Milestones.map((milestone) => {
                    const Icon = MILESTONE_ICONS[milestone.icon] || Trophy;
                    return (
                      <div key={milestone.month} className="milestone-item">
                        <div className="milestone-icon-wrapper">
                          <Icon className="milestone-icon" />
                        </div>
                        <div className="milestone-content">
                          <p className="milestone-month">Mes {milestone.month}</p>
                          <p className="milestone-title">{milestone.title}</p>
                          <p className="milestone-reward">{milestone.reward}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="challenge-cta-box">
                <p className="text-sm text-gray-300 mb-2">
                  🏆 Al completar el reto de 18 meses, habrás creado un hábito sólido que te acompañará toda la vida
                </p>
                <Button
                  className="button-neon w-full"
                  onClick={() => scrollToSection('productos')}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Quiero empezar el reto de 18 meses
                </Button>
              </div>

              <ShareButtons
                text={`Voy por el Reto 18 Meses: $${parseFloat(challenge18.monthlyAmount || 0).toLocaleString('es-MX')}/mes durante 18 meses = $${Math.round(challenge18.withInterest).toLocaleString('es-MX')} con rendimiento 💪 ¿Te animas conmigo?`}
                label="Invita a alguien a hacer el reto contigo:"
                testIdPrefix="challenge18-share"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  </section>
);

export default ChallengesSection;
